import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography, styled } from "@mui/material";
import { ChatConversation, useAdminChatHistory, useViewerActivity } from "./useAdminChats";

const MessageBody = styled(Typography)(({ theme }) => ({
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.action.hover,
}));

export function ChatViewer({ conversation }: { conversation: ChatConversation }) {
  const { history, older, read } = useAdminChatHistory(conversation.id);
  const active = useViewerActivity();
  const viewport = useRef<HTMLDivElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const beforePrepend = useRef<{ element: HTMLElement; offset: number } | null>(null);
  const [atEnd, setAtEnd] = useState(false);
  const messages = history.data?.messages || [];
  const latest = messages[messages.length - 1]?.sequence || 0;
  const first = messages[0]?.sequence;

  useEffect(() => {
    if (!end.current || !viewport.current) return;
    const observer = new IntersectionObserver(([entry]) => setAtEnd(entry.isIntersecting), {
      root: viewport.current,
      threshold: 0,
    });
    observer.observe(end.current);
    return () => observer.disconnect();
  }, [history.isPending, history.isError]);

  useLayoutEffect(() => {
    const node = viewport.current;
    if (!node) return;
    if (beforePrepend.current) {
      // A message anchor excludes appended rows, including batched prepend/catch-up responses.
      const { element, offset } = beforePrepend.current;
      node.scrollTop +=
        element.getBoundingClientRect().top - node.getBoundingClientRect().top - offset;
      if (!older.isPending) beforePrepend.current = null;
    } else if (pinned.current) {
      node.scrollTop = node.scrollHeight;
    }
    // Recalculate after layout; an old intersection result must not mark new offscreen messages read.
    setAtEnd(node.scrollHeight - node.scrollTop - node.clientHeight <= 1);
  }, [latest, first, history.isPending, older.isPending]);

  useEffect(() => {
    const node = viewport.current;
    if (
      node &&
      node.clientHeight > 0 &&
      node.scrollHeight - node.scrollTop - node.clientHeight <= 1 &&
      !document.hidden &&
      document.hasFocus() &&
      active &&
      atEnd &&
      !history.isError &&
      !history.isFetching &&
      !read.isPending &&
      !read.isError &&
      latest > Math.max(conversation.read_sequence, read.data || 0)
    ) {
      read.mutate(latest);
    }
  }, [
    active,
    atEnd,
    latest,
    conversation.read_sequence,
    history.isError,
    history.isFetching,
    read,
  ]);

  if (history.isError)
    return (
      <Alert severity="warning">
        {[403, 404].includes(history.error.response?.status || 0)
          ? "This conversation is no longer available."
          : "Unable to load this conversation."}
        <Button onClick={() => void history.refetch()}>Retry</Button>
      </Alert>
    );
  if (history.isPending) return <CircularProgress aria-label="Loading messages" />;

  return (
    <Stack minHeight={0} flex={1} gap={1}>
      <Typography variant="body2" color="text.secondary">
        Read-only · {conversation.participants.map((p) => p.name).join(" · ")}
      </Typography>
      {read.isError && (
        <Alert severity="warning">
          Could not save your read position.<Button onClick={() => read.reset()}>Retry</Button>
        </Alert>
      )}
      {older.isError && <Alert severity="warning">Could not load older messages.</Alert>}
      <Box
        ref={viewport}
        role="region"
        aria-label="Conversation messages"
        tabIndex={0}
        onScroll={() => {
          const node = viewport.current;
          if (node) {
            // Preserve a user's new position if they scroll while older history is loading.
            if (beforePrepend.current) {
              beforePrepend.current.offset =
                beforePrepend.current.element.getBoundingClientRect().top -
                node.getBoundingClientRect().top;
            }
            pinned.current = node.scrollHeight - node.scrollTop - node.clientHeight <= 1;
            setAtEnd(pinned.current);
          }
        }}
        sx={{ overflowY: "auto", minHeight: 0, flex: 1, overflowAnchor: "none" }}
      >
        {history.data?.hasOlder && (
          <Button
            disabled={older.isPending}
            onClick={() => {
              const node = viewport.current;
              if (node) {
                const top = node.getBoundingClientRect().top;
                const element = Array.from(
                  node.querySelectorAll<HTMLElement>("[data-message-id]"),
                ).find((row) => row.getBoundingClientRect().bottom > top);
                if (element)
                  beforePrepend.current = {
                    element,
                    offset: element.getBoundingClientRect().top - top,
                  };
              }
              older.mutate(undefined, {
                onError: () => {
                  beforePrepend.current = null;
                },
              });
            }}
          >
            Load older messages
          </Button>
        )}
        {!messages.length && <Typography color="text.secondary">No messages yet.</Typography>}
        <Stack gap={2} py={1}>
          {messages.map((message) => (
            <Box key={message.id} data-message-id={message.id}>
              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="baseline">
                <Typography variant="subtitle2">
                  {conversation.participants.find((p) => p.account_id === message.sender_account_id)
                    ?.name || "Former account"}
                </Typography>
                <Typography
                  component="time"
                  dateTime={message.created_at}
                  variant="caption"
                  color="text.secondary"
                >
                  {new Date(message.created_at).toLocaleString()}
                </Typography>
              </Stack>
              <MessageBody>{message.body}</MessageBody>
            </Box>
          ))}
        </Stack>
        <Box ref={end} sx={{ height: "1px" }} />
      </Box>
      {!atEnd && (
        <Button
          onClick={() => {
            if (viewport.current) {
              pinned.current = true;
              viewport.current.scrollTop = viewport.current.scrollHeight;
              setAtEnd(true);
            }
          }}
        >
          Jump to latest messages
        </Button>
      )}
    </Stack>
  );
}
